/**
 * @class NeptuneElement
 * @extends HTMLElement
 *
 * Base class for Neptune web components.
 * Provides common functionality for custom elements including:
 *  - Shadow DOM management
 *  - Template and style loading
 *  - Event handling and emission
 *  - Error handling and validation
 *  - Lifecycle management
 *
 * You can have an external template for complex markup or internal template for simple markup.
 * You should never use internal CSS — always use an external CSS file.
 * Stub methods are expected to be implemented by child classes.
 */
class NeptuneElement extends HTMLElement {

    /** @type {Map<string, Promise<CSSStyleSheet>>} Shared stylesheet cache keyed by URL, stored on `window` so it persists across module reloads. */
    static #stylesheetCache = (window.__neptuneStylesheetCache ??= new Map());
    /** @type {Map<string, Promise<string>>} Shared template fetch-promise cache keyed by URL, so each template is fetched only once. */
    static #templatePromises = (window.__neptuneTemplatePromises ??= new Map());
    /** @type {Map<string, DocumentFragment>} Shared parsed template fragment cache keyed by processed markup string, avoiding redundant DOM parsing. */
    static #parsedTemplateCache = (window.__neptuneParsedTemplateCache ??= new Map());

    /** @type {string} Inline template markup. Used as the template source when no `templateUrl` is provided. */
    templateMarkup = '';
    /** @type {string} URL to an external HTML template file. Optional — takes precedence over `templateMarkup` when set. */
    templateUrl = ''; // This is optional.
    /** @type {string} URL to a component-specific CSS file. Optional. */
    componentCssUrl = ''; // This is optional.
    /** @type {string} URL to the Neptune base stylesheet. Resolved from `window.__neptuneConfig.neptuneCssUrl` when set (e.g. by the CDN bootstrap), otherwise falls back to the local dev-server path. */

    neptuneCSSUrl = window.__neptuneConfig?.neptuneCssUrl ?? '/src/styles/neptune-without-normalize.css';
    /** @type {boolean} Set to `true` once the component has been fully rendered into the shadow DOM. */

    normalizeShadowCssUrl = window.__neptuneConfig?.normalizeShadowCssUrl ?? '/src/styles/normalize/normalize-shadow.css';

    isRendered = false;

    /**
     * Standard lifecycle custom events dispatched by Neptune components.
     *
     * @returns {{
     *   componentLoaded: string,
     *   componentUnloaded: string,
     *   componentReady: string,
     *   componentUpdated: string,
     *   componentFailed: string
     * }}
     */
     get CUSTOM_EVENTS() {
        return {
            componentLoaded: 'componentLoaded',
            componentUnloaded: 'componentUnloaded',
            componentReady: 'componentReady',
            componentUpdated: 'componentUpdated',
            componentFailed: 'componentFailed'
        }
    };

    /**
     * Error event names that can be dispatched via {@link emitErrorEvent}.
     *
     * @returns {{
     *   error: string,
     *   wrongPropertyValue: string,
     *   requiredPropertyMissing: string,
     *   propertyValidationFailed: string,
     *   dataValidationFailed: string,
     *   settingsValidationFailed: string,
     *   componentInitializingFailed: string,
     *   templateLoadFailed: string,
     *   styleLoadFailed: string
     * }}
     */
     get ERROR_EVENTS() {
        return {
            error: 'error',
            wrongPropertyValue: 'wrongPropertyValue',
            requiredPropertyMissing: 'requiredPropertyMissing',
            propertyValidationFailed: 'propertyValidationFailed',
            dataValidationFailed: 'dataValidationFailed',
            settingsValidationFailed: 'settingsValidationFailed',
            componentInitializingFailed: 'componentInitializingFailed',
            templateLoadFailed: 'templateLoadFailed',
            styleLoadFailed: 'styleLoadFailed'
        };
    };

    /**
     * Human-readable error message prefixes used when logging and throwing errors.
     *
     * @returns {{
     *   REQUIRED_PROPERTY: string,
     *   WRONG_PROPERTY_VALUE: string,
     *   TEMPLATE_LOAD_ERROR: string,
     *   STYLE_LOAD_ERROR: string,
     *   NEPTUNE_STYLE_LOAD_ERROR: string,
     *   INITIALIZATION_ERROR: string
     * }}
     */
     get MESSAGES() {
        return {
            REQUIRED_PROPERTY: 'NEPTUNE - Required property is missing. ',
            WRONG_PROPERTY_VALUE: 'NEPTUNE - Property value is not recognized or invalid. ',
            TEMPLATE_LOAD_ERROR: 'NEPTUNE - Failed to load component template. ',
            STYLE_LOAD_ERROR: 'NEPTUNE - Failed to load component styles. ',
            NEPTUNE_STYLE_LOAD_ERROR: 'NEPTUNE - Failed to load Neptune styles. ',
            INITIALIZATION_ERROR: 'NEPTUNE - Component initialization failed. '
        }
    };

    /**
     * Creates a new NeptuneElement instance.
     * Delegates to the HTMLElement constructor — subclasses must call `super()` before
     * accessing `this`.
     */
    constructor() {
        super();
    }

    /**
     * Called when the element is added to the DOM.
     * Renders the component, attaches event listeners, and emits `componentLoaded`.
     *
     * @returns {Promise<void>}
     */
    async connectedCallback() {
        if (!this.isRendered) { 
            await this.initializeDomReferences();
            await this.render();            
            await this.setEventListeners();            
            this.emitCustomEvent(this.CUSTOM_EVENTS.componentLoaded);         
            this.toggleAttribute('ready');            
        }        
    }

    /**
     * Called when the element is removed from the DOM.
     * Removes event listeners and emits `componentUnloaded`.
     *
     * @returns {Promise<void>}
     */
    async disconnectedCallback() {
        await this.removeEventListeners();
        this.emitCustomEvent(this.CUSTOM_EVENTS.componentUnloaded);
    }

    /**
     * Renders the component into a shadow DOM.
     *
     * Rendering order:
     * 1. Fetches the external HTML template from `templateUrl` (if set), otherwise uses `templateMarkup`.
     * 2. Passes the raw markup through `processTemplateMarkup` for any subclass-specific transformations.
     * 3. Parses the processed markup once and caches the resulting `DocumentFragment` by markup string
     *    to avoid redundant DOM parsing across component instances.
     * 4. Attaches an open shadow root and appends a clone of the cached fragment.
     * 5. Loads the Neptune base stylesheet (`neptuneCSSUrl`).
     * 6. Loads the component-specific stylesheet from `componentCssUrl`.
     * 7. Sets `isRendered` to `true` and emits `componentReady`.
     *
     * @returns {Promise<void>}
     * @throws {Error} If the external template or any stylesheet fails to load.
     */
    async render() {
        let markup = this.templateMarkup;

        if (this.templateUrl) {
            try {
                markup = await this.loadTemplate(this.templateUrl);
            } catch (error) {
                console.error(this.MESSAGES.TEMPLATE_LOAD_ERROR, error);
                this.emitCustomEvent(this.CUSTOM_EVENTS.componentFailed);
                throw new Error(this.MESSAGES.TEMPLATE_LOAD_ERROR);
            }
        }

        /* Cache parsed template fragments by processed markup string to avoid
           reparsing identical templates for every component instance. */
        let cachedFragment = NeptuneElement.#parsedTemplateCache.get(markup);
        if (!cachedFragment) {
            /* Avoids directly assigning raw HTML to innerHTML for XSS attacks. */
            const parser = new DOMParser();
            const parsed = parser.parseFromString(markup, 'text/html');
            const template = document.createElement('template');
            while (parsed.body.firstChild) {
                template.content.appendChild(document.adoptNode(parsed.body.firstChild));
            }
            cachedFragment = template.content;
            NeptuneElement.#parsedTemplateCache.set(markup, cachedFragment);
        }

        this.attachShadow({mode: 'open'});
        this.shadowRoot.appendChild(cachedFragment.cloneNode(true));

        /* Load Neptune CSS. */
        if (this.neptuneCSSUrl) {
            try {
                await this.loadStyle(this.neptuneCSSUrl);                
                if (this.neptuneCSSUrl.includes('without-normalize') && this.normalizeShadowCssUrl) {
                    await this.loadStyle(this.normalizeShadowCssUrl);
                }
            } catch (error) {
                console.error(this.MESSAGES.NEPTUNE_STYLE_LOAD_ERROR, error);
                this.emitCustomEvent(this.CUSTOM_EVENTS.componentFailed);
                throw new Error(this.MESSAGES.NEPTUNE_STYLE_LOAD_ERROR);
            }
        }

        /* Load component-specific CSS. */
        const componentCssUrl = this.componentCssUrl;
        if (componentCssUrl) {
            try {
                await this.loadStyle(componentCssUrl);
            } catch (error) {
                console.error(this.MESSAGES.STYLE_LOAD_ERROR, error);
                this.emitCustomEvent(this.CUSTOM_EVENTS.componentFailed);
                throw new Error(this.MESSAGES.STYLE_LOAD_ERROR);
            }
        }

        this.isRendered = true;

        this.emitCustomEvent(this.CUSTOM_EVENTS.componentReady);
    }

    /* TODO discuss observability with Joe and Ritvik for the details. */
    /**
     * Dispatches an `ErrorEvent` from the shadow root with structured error information.
     * Also logs the error to the console.
     *
     * @param {string} errorEvent - Key from {@link NeptuneElement.ERROR_EVENTS} identifying the error type.
     * @param {string} errorMessage - Detailed message describing the error.
     * @returns {void}
     */
    emitErrorEvent(errorEvent, errorMessage) {

        this.shadowRoot.dispatchEvent(new ErrorEvent(this.ERROR_EVENTS[errorEvent], {
            bubbles: true,
            composed: true,
            cancelable: true,
            error: 'Neptune Component Error',
            detail: errorMessage || ''
        }));
        console.error(this.ERROR_EVENTS[errorEvent] + ' - ' + errorMessage);
    }

    /**
     * Dispatches a `CustomEvent` from the shadow root.
     *
     * @param {string} customEvent - Key from {@link NeptuneElement.CUSTOM_EVENTS} identifying the event type.
     * @param {*} [detail] - Optional payload attached to the event's `message` property.
     * @returns {void}
     */
    emitCustomEvent(customEvent, detail) {

        this.shadowRoot.dispatchEvent(new CustomEvent(this.CUSTOM_EVENTS[customEvent], {
            bubbles: true,
            composed: true,
            cancelable: true,
            message: detail || {}
        }));
    }

    /**
     * Initialize DOM references, like buttons, links in the component template.
     * To be implemented by child classes.
     *
     * @returns {void|Promise<void>}
     */
    initializeDomReferences() {
    }

    /**
     * Attaches event listeners for the component.
     * To be implemented by child classes.
     *
     * @returns {void|Promise<void>}
     */
    setEventListeners() {
    }

    /**
     * Removes event listeners for the component.
     * To be implemented by child classes.
     *
     * @returns {void|Promise<void>}
     */
    removeEventListeners() {
    }

    /**
     * Processes an initial set of attributes after the component has been rendered.
     * Should be called from `connectedCallback` (after render) and from
     * `attributeChangedCallback` to keep attribute handling consistent.
     *
     * Iterates over the provided attribute names and, for each one that is present on the
     * element, delegates to {@link processAttribute} with the current value.
     *
     * @param {string[]} [attributes=[]] - Names of attributes to evaluate.
     * @returns {void}
     */
    processInitialAttributesAfterRender(attributes = []) {
        for (let index = 0, numberOfAttributes = attributes.length; index < numberOfAttributes; index++) {
            if (null !== this.getAttribute(attributes[index])) {
                this.processAttribute(attributes[index], null, this.getAttribute(attributes[index]));
            }
        }
    }

    /**
     * Evaluates a single attribute change and updates the component accordingly.
     * To be implemented by child classes — this is the primary hook for reactive
     * attribute handling and re-rendering logic.
     *
     * @param {string} name - The attribute name.
     * @param {string|null} oldValue - The previous attribute value, or `null` on first render.
     * @param {string|null} newValue - The new attribute value.
     * @returns {void}
     */
    processAttribute(name, oldValue, newValue) {

    }

    /**
     * Validates that all required attributes are present on the element.
     * Emits a `requiredPropertyMissing` error event and throws if any are absent.
     *
     * @param {string[]} [requiredProperties=[]] - Names of required attributes to check.
     * @returns {true} Returns `true` when all required properties are present.
     * @throws {Error} If one or more required properties are missing.
     */
    validateRequiredProperties(requiredProperties = []) {
        let missingProps = requiredProperties.filter(property => {
            return !this.hasAttribute(property);
        });

        if (missingProps.length > 0) {
            this.emitErrorEvent(this.ERROR_EVENTS.requiredPropertyMissing, this.MESSAGES.REQUIRED_PROPERTY + `  Missing: ${missingProps.join(', ')}`);
            throw new Error(`${this.MESSAGES.REQUIRED_PROPERTY} Missing: ${missingProps.join(', ')}`);
        }

        return true;
    }

    /**
     * Validates that a property's value is within a set of allowed values.
     * Emits a `wrongPropertyValue` error event and throws if the value is not permitted.
     *
     * @param {string} propertyName - The name of the property being validated.
     * @param {*} propertyValue - The value to validate.
     * @param {Array} [allowedValues=[]] - The list of accepted values.
     * @returns {true} Returns `true` when the value is valid.
     * @throws {Error} If `propertyValue` is not included in `allowedValues`.
     */
    validatePropertyValue(propertyName, propertyValue, allowedValues = []) {
        if (!allowedValues.includes(propertyValue)) {
            this.emitErrorEvent(this.ERROR_EVENTS.wrongPropertyValue, this.MESSAGES.WRONG_PROPERTY_VALUE);
            throw new Error(`${this.MESSAGES.WRONG_PROPERTY_VALUE} Property: ${propertyName}, Value: ${propertyValue}, Allowed: [${allowedValues.join(', ')}]`);
        }

        return true;
    }

    /**
     * Rewrites relative `url(...)` references and sourcemap comments in CSS text
     * so they resolve correctly when the CSS is applied outside its original directory.
     * Absolute URLs (starting with `data:`, `https?://`, or `/`) are left unchanged.
     *
     * @param {string} cssText - The raw CSS text to process.
     * @param {string} sourceUrl - The URL the CSS was fetched from; used to derive the base directory.
     * @returns {string} The CSS text with relative URLs made absolute.
     */
    static #rewriteStyleUrls(cssText, sourceUrl) {
        const baseDirectory = sourceUrl.substring(0, sourceUrl.lastIndexOf('/') + 1);
        return cssText
            .replace(
                /url\(\s*(['"]?)((?!data:|https?:\/\/|\/)[^)'"]*)\1\s*\)/g,
                (match, quote, relativePath) => {
                    if (!relativePath) return match;
                    const normalizedPath = relativePath.startsWith('./') ? relativePath.slice(2) : relativePath;
                    return `url('${baseDirectory}${normalizedPath}')`;
                }
            )
            .replace(
                /\/\*#\s*sourceMappingURL=(\S+)\s*\*\//g,
                (match, mapUrl) => {
                    if (mapUrl.startsWith('data:') || mapUrl.startsWith('/') || /^https?:\/\//.test(mapUrl)) {
                        return match;
                    }
                    return `/*# sourceMappingURL=${baseDirectory}${mapUrl} */`;
                }
            );
    }

    /**
     * Resolves `@import` rules in CSS text by fetching each imported file, recursively
     * resolving its own imports, rewriting its relative URLs to be absolute, and replacing
     * the `@import` rule with the resulting inline content.
     *
     * `CSSStyleSheet.replace()` ignores `@import` rules by spec, so this step is required
     * to ensure imported stylesheets (such as Font Awesome) are actually applied inside
     * shadow DOM adopted stylesheets.
     *
     * Failed imports are silently removed rather than blocking the rest of the stylesheet.
     *
     * @param {string} cssText - The CSS text that may contain `@import` rules.
     * @param {string} sourceUrl - The URL the CSS was fetched from; used to resolve relative import paths.
     * @returns {Promise<string>} The CSS text with all `@import` rules replaced by their inlined content.
     */
    static async #resolveStyleImports(cssText, sourceUrl) {
        const baseDirectory = sourceUrl.substring(0, sourceUrl.lastIndexOf('/') + 1);
        const importRulePattern = /@import\s+(?:url\(\s*['"]?([^'")\s]+)['"]?\s*\)|['"]([^'"]+)['"])\s*[^;]*;/g;
        const importMatches = [...cssText.matchAll(importRulePattern)];

        if (importMatches.length === 0) return cssText;

        let resolvedCssText = cssText;

        for (const importMatch of importMatches) {
            const importedUrl = importMatch[1] || importMatch[2];
            if (!importedUrl) continue;

            const absoluteImportUrl = /^(https?:)?\/\//.test(importedUrl) || importedUrl.startsWith('/')
                ? importedUrl
                : `${baseDirectory}${importedUrl}`;

            try {
                const importResponse = await fetch(absoluteImportUrl);
                if (!importResponse.ok) {
                    resolvedCssText = resolvedCssText.replace(importMatch[0], '');
                    continue;
                }
                const importedCssText = await importResponse.text();
                const resolvedImportedCssText = await NeptuneElement.#resolveStyleImports(importedCssText, absoluteImportUrl);
                const rewrittenImportedCssText = NeptuneElement.#rewriteStyleUrls(resolvedImportedCssText, absoluteImportUrl);
                resolvedCssText = resolvedCssText.replace(importMatch[0], rewrittenImportedCssText);
            } catch {
                resolvedCssText = resolvedCssText.replace(importMatch[0], '');
            }
        }

        return resolvedCssText;
    }

    /**
     * Loads a CSS file into the component's shadow root.
     *
     * Source-path URLs (starting with `/src/` or `blob:`) are loaded via a
     * `<link rel="stylesheet">` element so the browser follows `@import` chains
     * natively. This is required in development where the source CSS references
     * Font Awesome via `@import`, because `CSSStyleSheet.replace()` ignores
     * `@import` rules by spec.
     *
     * Built or CDN URLs are loaded via `fetch` + `adoptedStyleSheets`, which shares
     * one parsed `CSSStyleSheet` object across all shadow root instances — more
     * efficient for production pages with many components. Built CSS has no
     * remaining `@import` rules so `replace()` works correctly.
     *
     * In jsdom (Jest) `<link>` elements do not fire load events, so the fetch +
     * `adoptedStyleSheets` path is always used there regardless of URL.
     *
     * @param {string} url - URL of the CSS file to load.
     * @returns {Promise<void>}
     * @throws {Error} If the stylesheet fails to load (fetch path only).
     */
    async loadStyle(url) {
        if (!url) return;

        /* jsdom does not fire load events on <link> elements, so use the
           fetch + adoptedStyleSheets path for all URLs in test environments. */
        const isTestEnvironment = typeof navigator !== 'undefined' && navigator.userAgent.includes('jsdom');

        const requiresLinkElement = !isTestEnvironment && (url.startsWith('/src/') || url.startsWith('blob:'));

        if (requiresLinkElement) {
            /* Source CSS (dev/Storybook): inject a <link> element so the browser
               follows the @import chain (neptune.css → icons.css → Font Awesome)
               natively. One network request is made and cached by the browser;
               each shadow root keeps its own <link> node. */
            if (!this.shadowRoot.querySelector(`link[href="${url}"]`)) {
                await new Promise((resolve) => {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = url;
                    link.addEventListener('load', resolve, { once: true });
                    link.addEventListener('error', resolve, { once: true });
                    this.shadowRoot.appendChild(link);
                });
            }
            return;
        }

        /* Built/CDN CSS (production) and jsdom: fetch once, share the parsed
           CSSStyleSheet across all shadow roots via adoptedStyleSheets. */
        if (!NeptuneElement.#stylesheetCache.has(url)) {
            const sheetPromise = fetch(url)
                .then(async response => {
                    if (!response.ok) {
                        throw new Error(this.ERROR_EVENTS.styleLoadFailed + ' ' + url);
                    }
                    const rawCssText = await response.text();
                    const importResolvedCssText = await NeptuneElement.#resolveStyleImports(rawCssText, url);
                    const fullyRewrittenCssText = NeptuneElement.#rewriteStyleUrls(importResolvedCssText, url);
                    const sheet = new CSSStyleSheet();
                    await sheet.replace(fullyRewrittenCssText);
                    return sheet;
                });
            /* Suppress unhandled-rejection detection: the rejection is re-thrown
               via the `await` below; without this, Node.js flags the cached promise
               as unhandled because it is a separate promise object from the one
               returned by this async function. */
            sheetPromise.catch(() => {});
            NeptuneElement.#stylesheetCache.set(url, sheetPromise);
        }

        this.shadowRoot.adoptedStyleSheets = [
            ...(this.shadowRoot.adoptedStyleSheets || []),
            await NeptuneElement.#stylesheetCache.get(url)
        ];
    }

    /**
     * Fetches an external HTML template and returns its text content.
     *
     * Each unique URL is fetched only once — subsequent calls reuse the cached promise,
     * avoiding redundant network requests when multiple instances share the same template.
     *
     * @param {string} url - URL of the HTML template file to load.
     * @returns {Promise<string>} The raw HTML markup of the template.
     * @throws {Error} If the fetch response is not OK.
     */
    async loadTemplate(url) {
        if (!NeptuneElement.#templatePromises.has(url)) {
            const templatePromise = fetch(url)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(this.MESSAGES.TEMPLATE_LOAD_ERROR + url);
                    }
                    return response.text();
                });

            NeptuneElement.#templatePromises.set(url, templatePromise);
        }

        return NeptuneElement.#templatePromises.get(url);
    }
}

export default NeptuneElement;